<script setup lang="ts">
const { VITE_BACKEND_URL } = import.meta.env

import { ref } from 'vue'

import { useVuelidate } from '@vuelidate/core'
import {
  required,
  numeric,
  alpha,
  minValue,
  maxValue,
  maxLength,
  minLength
} from '@vuelidate/validators'

import { initialState, state } from '../state'

import TextInput from './form_inputs/TextInput.vue'
import SelectInput from './form_inputs/SelectInput.vue'
import CheckboxInput from './form_inputs/CheckboxInput.vue'

import Check from '@material-symbols/svg-400/rounded/check.svg'
import ChevronRight from '@material-symbols/svg-400/rounded/chevron_right.svg'
import NumberTag from '@material-symbols/svg-400/rounded/tag.svg'
import Person from '@material-symbols/svg-400/rounded/person.svg'

import Loading from '@material-symbols/svg-400/rounded/progress_activity.svg'

const emit = defineEmits<{
  (event: 'onClickNext'): void
}>()

const rules = {
  age: { required, numeric, maxValue: maxValue(130), minValue: minValue(12) },
  sex: { required, alpha, maxLength: maxLength(1), minLength: minLength(1) },
  height: { required, numeric, maxValue: maxValue(300), minValue: minValue(0) },
  weight: { required, numeric, maxValue: maxValue(500), minValue: minValue(0) },
  smoker: {},
  diabetesHistory: {}
}

const v$ = useVuelidate(rules, state)

const loading = ref(false)
const errorMessage = ref('')

async function handleSubmit({ payload = state }) {
  if (!loading.value) {
    loading.value = true
    const encodedPayload = JSON.stringify(payload)
    console.log('Submitting payload', JSON.parse(encodedPayload))
    try {
      const response = await fetch(VITE_BACKEND_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: encodedPayload
      })
      if (response.status === 200) {
        loading.value = false
        errorMessage.value = ''
        emit('onClickNext')
      } else {
        loading.value = false
        const data = await response.json()
        if (data?.detail) {
          errorMessage.value = data.detail
        } else {
          errorMessage.value = 'Could not submit data, please try again later.'
        }
      }
    } catch {
      loading.value = false
      errorMessage.value = 'Could not submit data, please try again later.'
      // emit('onClickNext')
    }
    // for early demo
  }
}

async function onSubmit() {
  if (await v$.value.$validate()) {
    handleSubmit({ payload: state })
  }
}

function onSkip() {
  const minimal = {
    ...initialState,
    barcode: state.barcode,
    country: state.country,
    pinCode: state.pinCode,
    pinCodeConfirm: state.pinCodeConfirm
  }
  handleSubmit({ payload: minimal })
}
</script>
<template>
  <form @submit.prevent @submit="onSubmit" class="mt-4">
    <div class="space-y-3">
      <div class="flex items-center justify-between space-x-6">
        <div class="flex-grow flex-1">
          <TextInput
            id="age"
            v-model="state.age"
            :icon="NumberTag"
            label="Age"
            placeholder="Age"
            required
            :errors="v$.age.$errors.map<any>((e) => e.$message)"
          />
        </div>
        <div class="flex-grow flex-1">
          <SelectInput
            id="sex"
            v-model="state.sex"
            :icon="Person"
            :items="[
              { value: 'm', label: 'Male' },
              { value: 'f', label: 'Female' }
            ]"
            item-title="label"
            item-value="value"
            label="Sex"
            placeholder="Sex"
            required
            :errors="v$.sex.$errors.map<any>((e) => e.$message)"
          />
        </div>
      </div>
      <div class="flex items-center justify-between space-x-6">
        <div class="flex-grow flex-1">
          <TextInput
            id="height"
            v-model="state.height"
            :icon="NumberTag"
            label="Height"
            placeholder="Height"
            required
            :errors="v$.height.$errors.map<any>((e) => e.$message)"
          />
        </div>
        <div class="flex-grow flex-1">
          <TextInput
            id="weight"
            v-model="state.weight"
            :icon="NumberTag"
            label="Weight"
            placeholder="Weight"
            required
            :errors="v$.weight.$errors.map<any>((e) => e.$message)"
          />
        </div>
      </div>
      <div>
        <CheckboxInput
          id="smoker"
          v-model="state.smoker"
          :errors="v$.smoker.$errors.map<any>((e) => e.$message)"
        >
          I am a smoker.
        </CheckboxInput>
      </div>
      <div>
        <CheckboxInput
          id="diabetesHistory"
          v-model="state.diabetesHistory"
          :errors="v$.diabetesHistory.$errors.map<any>((e) => e.$message)"
        >
          There have been cases of type 2 diabetes in my family history.
        </CheckboxInput>
      </div>
    </div>
    <div class="text-center error-text" v-if="!!errorMessage">{{ errorMessage }}</div>
    <div class="flex justify-between mt-8 mb-4 text-sm">
      <button
        @click="onSkip"
        class="flex items-center justify-between rounded-md py-1 px-2 text-uppercase"
        type="button"
      >
        <span class="text-sky-400">Skip</span>
        <ChevronRight class="w-6 h-6 ml-2 fill-sky-400 dark:fill-sky-400" />
      </button>
      <button class="btn-next py-2 px-4" type="submit">
        Complete registration
        <Loading v-if="loading" class="animate-spin w-6 h-6 ml-2" />
        <Check v-if="!loading" class="w-4 h-4 ml-2" />
      </button>
    </div>
  </form>
</template>
